import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Button } from "@/components/ui/button";
import { isSafeRouteForPwaRefresh, PWA_JUST_UPDATED_KEY } from "@/lib/pwa-safe-routes";

/** Interval in ms to check for SW updates while app is visible */
const UPDATE_CHECK_INTERVAL = 60_000; // 1 minute

export function PwaUpdatePrompt({ hasBottomNav = false }: { hasBottomNav?: boolean }) {
  const location = useLocation();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;

      // Check on visibility change (returning from background)
      const onVisible = () => {
        if (document.visibilityState === "visible") {
          console.log("[PWA] visibility → visible, checking for update…");
          registration.update();
        }
      };
      document.addEventListener("visibilitychange", onVisible);

      // Periodic check while visible
      const interval = setInterval(() => {
        if (document.visibilityState === "visible") {
          console.log("[PWA] periodic update check…");
          registration.update();
        }
      }, UPDATE_CHECK_INTERVAL);

      // Note: no cleanup needed – this lives for the lifetime of the app
    },
    onNeedRefresh() {
      console.log("[PWA] onNeedRefresh fired – new version available");
    },
  });

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const autoRefreshDoneRef = useRef(false);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Auto-refresh on safe routes when update is available and online
  useEffect(() => {
    if (!needRefresh || !isOnline || autoRefreshDoneRef.current) return;
    if (!isSafeRouteForPwaRefresh(location.pathname)) return;

    autoRefreshDoneRef.current = true;
    sessionStorage.setItem(PWA_JUST_UPDATED_KEY, "1");
    updateServiceWorker(true);
  }, [needRefresh, isOnline, location.pathname, updateServiceWorker]);

  if (!needRefresh) return null;

  // Offline or unsafe route: show banner
  if (!isOnline || !isSafeRouteForPwaRefresh(location.pathname)) {
    return (
      <div className={`fixed left-4 right-4 z-[60] flex items-center justify-between gap-3 rounded-xl border bg-card p-4 shadow-lg ${hasBottomNav ? "bottom-[calc(3.5rem+env(safe-area-inset-bottom,0px)+1.25rem)]" : "bottom-[calc(env(safe-area-inset-bottom,0px)+1.25rem)]"}`}>
        <p className="text-sm text-card-foreground">
          {isOnline ? "Ny versjon klar – oppdater nå!" : "Koble til nett for å oppdatere"}
        </p>
        <Button
          size="sm"
          onClick={() => updateServiceWorker(true)}
          disabled={!isOnline}
          className="rounded-lg"
        >
          Oppdater
        </Button>
      </div>
    );
  }

  // Safe route + online: refresh already triggered in effect
  return null;
}
